import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DataPreviewModal = ({ dataset, onClose }) => {
  const [activeTab, setActiveTab] = useState('overview');

  if (!dataset) return null;

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'Info' },
    { id: 'metadata', label: 'Metadata', icon: 'FileText' },
    { id: 'preview', label: 'Data Preview', icon: 'Eye' },
    { id: 'citations', label: 'Citations', icon: 'Quote' }
  ];

  const sampleData = [
    { id: 1, species: 'Rafflesia speciosa', location: 'Zone A', count: 12, date: '2025-11-15' },
    { id: 2, species: 'Pithecophaga jefferyi', location: 'Zone B', count: 3, date: '2025-11-20' },
    { id: 3, species: 'Crocodylus mindorensis', location: 'Zone C', count: 8, date: '2025-11-25' },
    { id: 4, species: 'Cervus alfredi', location: 'Zone A', count: 15, date: '2025-12-01' },
    { id: 5, species: 'Sus cebifrons', location: 'Zone D', count: 22, date: '2025-12-05' }
  ];

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-[var(--color-card)] rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-[var(--color-border)]">
          <h2 className="text-2xl font-semibold text-[var(--color-card-foreground)]">
            {dataset?.title}
          </h2>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-[var(--color-muted)] rounded-lg animation-organic"
          >
            <Icon name="X" size={24} className="text-[var(--color-muted-foreground)]" />
          </button>
        </div>

        <div className="flex border-b border-[var(--color-border)]">
          {tabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center gap-2 px-6 py-4 border-b-2 animation-organic ${
                activeTab === tab?.id
                  ? 'border-[var(--color-primary)] text-[var(--color-primary)]'
                  : 'border-transparent text-[var(--color-muted-foreground)] hover:text-[var(--color-card-foreground)]'
              }`}
            >
              <Icon name={tab?.icon} size={18} />
              <span className="font-medium">{tab?.label}</span>
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-[var(--color-card-foreground)] mb-2">Description</h3>
                <p className="text-[var(--color-muted-foreground)] leading-relaxed">{dataset?.description}</p>
              </div>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h4 className="text-sm font-medium text-[var(--color-card-foreground)] mb-2">Dataset Information</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-[var(--color-muted-foreground)]">Format:</span>
                      <span className="text-sm text-[var(--color-card-foreground)] font-medium">{dataset?.format}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-[var(--color-muted-foreground)]">Size:</span>
                      <span className="text-sm text-[var(--color-card-foreground)] font-medium">
                        {(dataset?.size / (1024 * 1024))?.toFixed(2)} MB
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-[var(--color-muted-foreground)]">Last Updated:</span>
                      <span className="text-sm text-[var(--color-card-foreground)] font-medium">{dataset?.lastUpdated}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-[var(--color-muted-foreground)]">Downloads:</span>
                      <span className="text-sm text-[var(--color-card-foreground)] font-medium">{dataset?.downloads}</span>
                    </div>
                  </div>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-[var(--color-card-foreground)] mb-2">Access Information</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-[var(--color-muted-foreground)]">Access Level:</span>
                      <span className="text-sm text-[var(--color-card-foreground)] font-medium">{dataset?.accessLevel}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-[var(--color-muted-foreground)]">License:</span>
                      <span className="text-sm text-[var(--color-card-foreground)] font-medium">CC BY 4.0</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-[var(--color-muted-foreground)]">DOI:</span>
                      <span className="text-sm text-[var(--color-primary)] font-medium">10.5281/zenodo.123456</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'metadata' && (
            <div className="space-y-4">
              <div className="bg-[var(--color-muted)] rounded-lg p-4">
                <h4 className="text-sm font-medium text-[var(--color-card-foreground)] mb-2">Temporal Coverage</h4>
                <p className="text-sm text-[var(--color-muted-foreground)]">January 2023 - December 2025</p>
              </div>
              <div className="bg-[var(--color-muted)] rounded-lg p-4">
                <h4 className="text-sm font-medium text-[var(--color-card-foreground)] mb-2">Geographic Coverage</h4>
                <p className="text-sm text-[var(--color-muted-foreground)]">Puerto Princesa Subterranean River National Park, Palawan, Philippines</p>
              </div>
              <div className="bg-[var(--color-muted)] rounded-lg p-4">
                <h4 className="text-sm font-medium text-[var(--color-card-foreground)] mb-2">Methodology</h4>
                <p className="text-sm text-[var(--color-muted-foreground)]">
                  Field surveys conducted quarterly using standardized protocols. Data collected through direct observation, camera traps, and acoustic monitoring. Quality control performed by certified researchers.
                </p>
              </div>
              <div className="bg-[var(--color-muted)] rounded-lg p-4">
                <h4 className="text-sm font-medium text-[var(--color-card-foreground)] mb-2">Contributors</h4>
                <p className="text-sm text-[var(--color-muted-foreground)]">
                  Dr. Maria Santos (Lead Researcher), Dr. Juan Cruz (Field Coordinator), PPSRNP Research Team
                </p>
              </div>
            </div>
          )}

          {activeTab === 'preview' && (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[var(--color-border)]">
                    <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-card-foreground)]">ID</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-card-foreground)]">Species</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-card-foreground)]">Location</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-card-foreground)]">Count</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-card-foreground)]">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {sampleData?.map((row) => (
                    <tr key={row?.id} className="border-b border-[var(--color-border)] hover:bg-[var(--color-muted)] animation-organic">
                      <td className="py-3 px-4 text-sm text-[var(--color-muted-foreground)]">{row?.id}</td>
                      <td className="py-3 px-4 text-sm text-[var(--color-card-foreground)]">{row?.species}</td>
                      <td className="py-3 px-4 text-sm text-[var(--color-muted-foreground)]">{row?.location}</td>
                      <td className="py-3 px-4 text-sm text-[var(--color-muted-foreground)]">{row?.count}</td>
                      <td className="py-3 px-4 text-sm text-[var(--color-muted-foreground)]">{row?.date}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-sm text-[var(--color-muted-foreground)] mt-4 text-center">
                Showing 5 of 1,247 records. Download full dataset to access all data.
              </p>
            </div>
          )}

          {activeTab === 'citations' && (
            <div className="space-y-6">
              <div>
                <h4 className="text-sm font-medium text-[var(--color-card-foreground)] mb-3">Recommended Citation</h4>
                <div className="bg-[var(--color-muted)] rounded-lg p-4">
                  <p className="text-sm text-[var(--color-card-foreground)] font-mono">
                    Santos, M., Cruz, J., & PPSRNP Research Team. (2025). {dataset?.title}. Puerto Princesa Subterranean River National Park Digital Platform. https://doi.org/10.5281/zenodo.123456
                  </p>
                  <Button 
                    variant="outline" 
                    size="sm"
                    iconName="Copy"
                    iconPosition="left"
                    className="mt-3"
                  >
                    Copy Citation
                  </Button>
                </div>
              </div>
              <div>
                <h4 className="text-sm font-medium text-[var(--color-card-foreground)] mb-3">BibTeX Format</h4>
                <div className="bg-[var(--color-muted)] rounded-lg p-4">
                  <pre className="text-xs text-[var(--color-card-foreground)] font-mono overflow-x-auto">
{`@dataset{santos2025biodiversity,
  author = {Santos, Maria and Cruz, Juan and PPSRNP Research Team},
  title = {${dataset?.title}},
  year = {2025},
  publisher = {PPSRNP Digital Platform},
  doi = {10.5281/zenodo.123456}
}`}
                  </pre>
                  <Button 
                    variant="outline" 
                    size="sm"
                    iconName="Copy"
                    iconPosition="left"
                    className="mt-3"
                  >
                    Copy BibTeX
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-[var(--color-border)]">
          <Button 
            variant="outline"
            onClick={onClose}
          >
            Close
          </Button>
          <Button 
            variant="default"
            iconName="Download"
            iconPosition="left"
          >
            Download Dataset
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DataPreviewModal;